import { useEffect, useMemo } from 'react';
import type { RefObject } from 'react';
import type { WorkbookInstance } from '@fortune-sheet/react';
import type { FortuneCell, FortuneSheet } from './types';
import FitColumnsIcon from '../../assets/Svg/FitColumnsIcon';
import { liveDims } from './excel/navigation';
import { getInternals } from './excel/workbookInternals';

// Mirrors @fortune-sheet/core getFontSet for the default cell: "<fs>pt" of this
// stack. We measure with the same string so widths match what the grid renders.
const FONT_STACK =
    '"Helvetica Neue", Helvetica, Arial, "PingFang SC", "Hiragino Sans GB", "Heiti SC", "Microsoft YaHei", "WenQuanYi Micro Hei", sans-serif';
const DEFAULT_FONT_PT = 10; // fortune-sheet defaultFontSize
const CELL_FONT = `normal normal normal ${DEFAULT_FONT_PT}pt ${FONT_STACK}`;
const CELL_PADDING_PX = 16; // left+right cell padding plus slack so text sits on ONE line, never wraps
const MIN_COL_WIDTH = 40;
// Generous cap so wide content widens the column to fit on one line instead of
// wrapping; only an extreme outlier is bounded (keeps the column usable).
const MAX_COL_WIDTH = 800;
const ROW_HEADER_WIDTH = 46; // fortune-sheet default (rowHeaderWidth is not overridden)
const BOUNDARY_TOLERANCE_PX = 6;
const GRIDLINE_PX = 1; // Fortune draws a 1px line after every column: an edge sits at width + 1 per column

/**
 * CSS font string matching how Fortune-Sheet renders THIS cell (weight / style /
 * size / family). Autofit must measure with the cell's own font — a bold or
 * larger cell measured at the plain default font comes out too narrow, so the
 * fitted column is undersized and the text wraps. Measuring per cell keeps every
 * value on one line at the fitted width.
 */
/** Exported for tests. */
export const cellFont = (cell: FortuneCell | null): string => {
    const style = cell?.it ? 'italic' : 'normal';
    const weight = cell?.bl ? 'bold' : 'normal';
    const size = typeof cell?.fs === 'number' && cell.fs > 0 ? cell.fs : DEFAULT_FONT_PT;
    const family = cell?.ff ? `"${String(cell.ff)}", ${FONT_STACK}` : FONT_STACK;
    return `${style} normal ${weight} ${size}pt ${family}`;
};

let measureCtx: CanvasRenderingContext2D | null | undefined;
const getMeasureCtx = (): CanvasRenderingContext2D | null => {
    if (measureCtx === undefined) {
        measureCtx = document.createElement('canvas').getContext('2d');
        if (measureCtx) measureCtx.font = CELL_FONT;
    }
    return measureCtx;
};

const cellText = (cell: FortuneCell | null): string => {
    if (cell == null) return '';
    if (cell.m != null) return String(cell.m);
    if (cell.v != null) return String(cell.v);
    return '';
};

/** Final column width from a measured max text width: add padding, clamp to a sane range. */
export const clampWidth = (rawTextWidth: number): number =>
    Math.min(MAX_COL_WIDTH, Math.max(MIN_COL_WIDTH, Math.ceil(rawTextWidth + CELL_PADDING_PX)));

/** Columns (0-based) that contain at least one non-empty cell. */
export const contentColumns = (sheet: FortuneSheet): number[] => {
    const cols = new Set<number>();
    if (sheet.celldata?.length) {
        sheet.celldata.forEach(({ c, v }) => {
            if (cellText(v) !== '') cols.add(c);
        });
    } else if (sheet.data) {
        sheet.data.forEach(
            (row) =>
                row?.forEach((cell, c) => {
                    if (cellText(cell) !== '') cols.add(c);
                }),
        );
    }
    return [...cols].sort((a, b) => a - b);
};

/**
 * Given each column's pixel width and an x offset into the full column space,
 * return the column whose RIGHT edge sits within `tol` of x — the boundary that
 * was double-clicked — or -1 when the click wasn't on a boundary.
 */
export const findBoundaryColumn = (
    widths: Record<number, number>,
    colCount: number,
    xInContent: number,
    tol: number = BOUNDARY_TOLERANCE_PX,
): number => {
    let cumulative = 0;
    for (let i = 0; i < colCount; i += 1) {
        cumulative += (widths[i] ?? 0) + GRIDLINE_PX;
        if (Math.abs(cumulative - xInContent) <= tol) return i;
        if (cumulative - xInContent > tol) break;
    }
    return -1;
};

/** Exported for tests. */
export const measureColumnWidths = (sheet: FortuneSheet, cols: Set<number>): Record<number, number> => {
    const ctx = getMeasureCtx();
    // Pre-seed every requested column with the minimum width so empty selected
    // columns still get an explicit width (not silently skipped).
    const widths: Record<number, number> = {};
    cols.forEach((c) => {
        widths[c] = clampWidth(0);
    });
    if (!ctx) return widths;
    const maxText: Record<number, number> = {};
    const visit = (c: number, cell: FortuneCell | null) => {
        if (!cols.has(c)) return;
        const text = cellText(cell);
        if (!text) return;
        // Measure with this cell's own font so bold/large cells get an accurate
        // (wider) width — otherwise the fitted column is too narrow and wraps.
        ctx.font = cellFont(cell);
        // Widest single line, so a value with embedded newlines doesn't force an
        // over-wide column while still fitting its longest line.
        const width = text.split('\n').reduce((max, line) => Math.max(max, ctx.measureText(line).width), 0);
        if (width > (maxText[c] ?? 0)) maxText[c] = width;
    };
    if (sheet.celldata?.length) {
        sheet.celldata.forEach(({ c, v }) => visit(c, v));
    } else if (sheet.data) {
        sheet.data.forEach((row) => row?.forEach((cell, c) => visit(c, cell)));
    }
    Object.entries(maxText).forEach(([c, w]) => {
        widths[Number(c)] = clampWidth(w);
    });
    return widths;
};

// The tab the user is on. `status` is set once at load and never follows a tab switch,
// so on any other tab it measured the first sheet's content.
const activeSheet = (wb: WorkbookInstance): FortuneSheet | undefined => {
    const sheets = wb.getAllSheets();
    const activeId = getInternals(wb)?.getContext().currentSheetId;
    return sheets.find((s) => String(s.id) === String(activeId)) ?? sheets.find((s) => s.status === 1) ?? sheets[0];
};

const fitColumns = (wb: WorkbookInstance, cols: number[]): void => {
    if (!cols.length) return;
    const sheet = activeSheet(wb);
    if (!sheet) return;
    const widths = measureColumnWidths(sheet, new Set(cols));
    if (Object.keys(widths).length) wb.setColumnWidth(widths, undefined, true);
};

interface UseColumnAutofitArgs {
    workbookRef: RefObject<WorkbookInstance>;
    wrapperRef: RefObject<HTMLDivElement>;
    /** True once the Workbook is mounted, so the listener attaches to the live DOM. */
    enabled: boolean;
}

/**
 * Adds Excel-style "fit column to content": double-click a column border to size
 * it to its widest cell, plus a "Fit columns" toolbar button (selected columns,
 * or all with content). Fortune-Sheet has no native autofit, so the double-click
 * maps the click x to a column boundary via the live grid geometry and the
 * library's scroll element; the toolbar button is selection-based (no geometry).
 */
export const useColumnAutofit = ({ workbookRef, wrapperRef, enabled }: UseColumnAutofitArgs) => {
    useEffect(() => {
        const root = wrapperRef.current;
        if (!enabled || !root) return undefined;

        const onDoubleClick = (e: MouseEvent) => {
            const target = e.target as HTMLElement | null;
            const headerWrap = target?.closest('.fortune-col-header-wrap') as HTMLElement | null;
            if (!headerWrap) return; // only the column-header strip
            const wb = workbookRef.current;
            const sheet = wb ? activeSheet(wb) : undefined;
            const colCount = sheet ? liveDims(sheet).cols : 0;
            if (!wb || !colCount) return;

            // Column 0's left origin = the RIGHT edge of the row-header strip
            // (that's exactly where the column content begins), which is robust to
            // theme/zoom/layout — unlike a hardcoded row-header width or assuming
            // the scrollbar shares the column origin. Fall back to the scrollbar /
            // header-wrap + ROW_HEADER_WIDTH only if the row header isn't found.
            const scrollX = root.querySelector('.luckysheet-scrollbar-x') as HTMLElement | null;
            const rowHeader = root.querySelector('.fortune-row-header') as HTMLElement | null;
            const originLeft = rowHeader
                ? rowHeader.getBoundingClientRect().right
                : scrollX
                ? scrollX.getBoundingClientRect().left
                : headerWrap.getBoundingClientRect().left + ROW_HEADER_WIDTH;
            const scrollLeft = scrollX?.scrollLeft ?? 0;
            const xInContent = e.clientX - originLeft + scrollLeft;

            // The library's own right edges (gridline, zoom and hidden columns included). Summing
            // plain widths drifted 1px per column and stopped matching from about column G.
            const edges = getInternals(wb)?.getContext().visibledatacolumn;
            const col = edges?.length
                ? edges.findIndex((edge) => Math.abs(edge - xInContent) <= BOUNDARY_TOLERANCE_PX)
                : findBoundaryColumn(wb.getColumnWidth(Array.from({ length: colCount }, (_, i) => i)), colCount, xInContent);
            if (col < 0) return; // not on a boundary — let the normal header dblclick proceed

            e.preventDefault();
            e.stopPropagation();
            fitColumns(wb, [col]);
        };

        // Capture phase: run before fortune-sheet's own header handlers.
        root.addEventListener('dblclick', onDoubleClick, true);
        return () => root.removeEventListener('dblclick', onDoubleClick, true);
    }, [enabled, workbookRef, wrapperRef]);

    // Referentially stable across renders — the onClick reads workbookRef.current
    // (a ref, always the same object) rather than closing over any render-scoped
    // value, so an empty dep array never goes stale. A new object/array identity
    // here would otherwise flow into <Workbook customToolbarItems>, whose
    // mergedSettings memo re-runs a full cloneDeep + re-inits on ANY new prop
    // identity — i.e. a fresh object every render defeats memoization entirely.
    const fitColumnsToolbarItem = useMemo(
        () => ({
            key: 'fit-columns',
            tooltip: 'Fit column width to content',
            icon: <FitColumnsIcon />,
            onClick: () => {
                const wb = workbookRef.current;
                const sheet = wb ? activeSheet(wb) : undefined;
                if (!wb || !sheet) return;
                const selected = new Set<number>();
                (wb.getSelection?.() ?? []).forEach((range) => {
                    const [c1, c2] = range.column;
                    for (let c = Math.min(c1, c2); c <= Math.max(c1, c2); c += 1) selected.add(c);
                });
                const targets = selected.size ? [...selected] : contentColumns(sheet);
                fitColumns(wb, targets);
            },
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );

    return { fitColumnsToolbarItem };
};
